/**
 * UserAvatar — exibe foto do usuario com fallback de iniciais coloridas.
 *
 * Iniciais sao derivadas do `nome` ou `email`. A cor de fundo e calculada
 * a partir de um hash simples para que o mesmo usuario sempre apareca
 * com a mesma cor (consistencia visual = pista cognitiva).
 *
 * Props:
 * - `src` (string opcional) — URL da foto. Se vazio ou erro de load,
 *   cai para iniciais.
 * - `nome` (string)
 * - `email` (string opcional, fallback se nome ausente)
 * - `size` (xs|sm|md|lg|xl) — default md
 */
import React, { useEffect, useState } from "react";

import { useAuthenticatedImageUrl } from "../../hooks/useAuthenticatedImageUrl";
import { AvatarImage, AvatarWrapper } from "./styles.jsx";

const PALETA = [
  "#6366F1", // indigo
  "#0EA5E9", // sky
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#14B8A6", // teal
];

function obterIniciais(nome, email) {
  const fonte = (nome || email || "?").trim();
  if (!fonte) return "?";
  const partes = fonte.split(/[\s.@_-]+/).filter(Boolean);
  if (partes.length === 0) return fonte[0].toUpperCase();
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

function corDeFundo(seed) {
  if (!seed) return PALETA[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return PALETA[Math.abs(hash) % PALETA.length];
}

function UserAvatar({ src, nome = "", email = "", size = "md", alt }) {
  const [erroImg, setErroImg] = useState(false);
  // O endpoint de foto (/api/usuarios/{id}/foto-arquivo) exige JWT no header —
  // <img src> puro tomaria 401. O hook busca com o Bearer e devolve um object URL
  // (data:/blob: passam direto; src vazio => "" => cai nas iniciais).
  const { imageUrl, failed } = useAuthenticatedImageUrl(src);
  useEffect(() => setErroImg(false), [imageUrl]);
  const usarFoto = Boolean(imageUrl) && !failed && !erroImg;
  const iniciais = obterIniciais(nome, email);
  const bg = corDeFundo(nome || email || "?");
  const aria = alt || (nome ? `Foto de ${nome}` : "Foto do usuário");

  return (
    <AvatarWrapper $bg={bg} $size={size} aria-label={aria}>
      {usarFoto ? (
        <AvatarImage
          src={imageUrl}
          alt={aria}
          onError={() => setErroImg(true)}
          loading="lazy"
        />
      ) : (
        <span aria-hidden="true">{iniciais}</span>
      )}
    </AvatarWrapper>
  );
}

export default UserAvatar;
